import { useState } from "react";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Nav } from "@/components/Nav";
import { Seo } from "@/components/Seo";
import { Footer } from "@/components/Footer";
import { FadeUp, SectionHeading } from "@/components/Reveal";

const KINDS = ["Accommodation provider", "Tour or experience operator", "Transport provider", "Other service business"];

export default function PartnerPage() {
  const [form, setForm] = useState({ name: "", email: "", kind: KINDS[0], message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/enquiries", { name: form.name, email: form.email, journey: `Partner — ${form.kind}`, message: form.message });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-jawa-ivory" data-testid="partner-page">
      <Nav />
      <Seo
        title="Partner With Us — Suppliers & Operators | JÀWÀ Africa"
        description="Accommodation providers, tour operators, transport and experience businesses: enquire about working with JÀWÀ Africa."
      />
      <main className="mx-auto max-w-7xl px-6 pb-24 pt-32 lg:px-10 lg:pt-40">
        <div className="grid gap-14 lg:grid-cols-2 lg:gap-20">
          <FadeUp>
            <SectionHeading overline="Partner with JÀWÀ" title="Great businesses make our recommendations." />
            <p className="mt-6 text-base leading-relaxed text-jawa-espresso/70">
              Our clients ask us for the best of Africa — stays, experiences, transport and services that
              actually deliver. If you run an accommodation, tour, transport or experience business that meets
              that bar, we'd like to hear from you.
            </p>
            <ul className="mt-6 space-y-3 text-sm leading-relaxed text-jawa-espresso/65">
              <li>— Tell us what you offer, where, and what makes it genuinely good.</li>
              <li>— We review every enquiry personally and visit or vet before recommending anyone.</li>
              <li>— No listing fees, no promises of referrals: recommendations are earned, never sold.</li>
            </ul>
            <p className="mt-6 text-xs leading-relaxed text-jawa-espresso/50">
              Submitting an enquiry starts a conversation — it does not create a partnership, verification or
              any commercial arrangement.
            </p>
          </FadeUp>

          <FadeUp delay={0.08}>
            {sent ? (
              <div className="border border-jawa-turquoise/40 bg-white p-10" data-testid="partner-success">
                <h2 className="font-serif text-2xl font-medium text-jawa-espresso">Thank you — enquiry received.</h2>
                <p className="mt-3 text-sm leading-relaxed text-jawa-espresso/65">
                  A member of the JÀWÀ team will review it and be in touch. We look forward to learning about
                  what you do.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4 border border-jawa-espresso/10 bg-white p-8" data-testid="partner-form">
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Your name / business name"
                  data-testid="partner-name"
                  className="w-full border border-jawa-espresso/20 bg-white px-4 py-3 text-sm outline-none focus:border-jawa-turquoise"
                />
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="Email address"
                  data-testid="partner-email"
                  className="w-full border border-jawa-espresso/20 bg-white px-4 py-3 text-sm outline-none focus:border-jawa-turquoise"
                />
                <select
                  value={form.kind}
                  onChange={(e) => setForm({ ...form, kind: e.target.value })}
                  data-testid="partner-kind"
                  className="w-full border border-jawa-espresso/20 bg-white px-4 py-3 text-sm outline-none focus:border-jawa-turquoise"
                >
                  {KINDS.map((k) => (
                    <option key={k} value={k}>{k}</option>
                  ))}
                </select>
                <textarea
                  required
                  minLength={20}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="What do you offer, where, and why is it good? (min 20 characters)"
                  rows={5}
                  data-testid="partner-message"
                  className="w-full border border-jawa-espresso/20 bg-white px-4 py-3 text-sm outline-none focus:border-jawa-turquoise"
                />
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="partner-submit"
                  className="flex w-full items-center justify-center gap-2 bg-jawa-espresso px-6 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-jawa-ivory transition-colors hover:bg-jawa-coral disabled:opacity-60"
                >
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                  Send partnership enquiry <ArrowUpRight className="h-4 w-4" />
                </button>
              </form>
            )}
          </FadeUp>
        </div>
      </main>
      <Footer />
    </div>
  );
}
